/* eslint-disable @typescript-eslint/no-explicit-any */
import React, {
  ChangeEvent,
  ReactNode,
  ReactElement,
  CSSProperties,
} from "react";
import { useFormContext, useController } from "react-hook-form";
import * as yup from "yup";

// Styling constants
const COMMON_INPUT_STYLES = "text-sm rounded-lg block w-full p-2";
const NORMAL_STATE =
  "bg-gray-50 border border-gray-300 text-gray-900 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500";
const ERROR_STATE =
  "bg-red-50 border border-red-500 text-red-900 placeholder-red-700 focus:ring-red-500 dark:bg-gray-700 focus:border-red-500 dark:text-red-500 dark:placeholder-red-500 dark:border-red-500";

interface InputProps {
  name: string;
  label: string;
  type?: string;
  validationSchema?: yup.AnyObjectSchema;
  placeholder?: string;
  onChange?: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onKeyDown?: (
    e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  value?: string | number;
  required?: boolean;
  disabled?: boolean;
  min?: any;
  max?: any;
  className?: string;
  noCapitalize?: boolean;
  icon?: ReactNode;
  rightIcon?: ReactElement;
  onRightIconClick?: () => void;
  helperText?: string;
  autoComplete?: string;
  maxLength?: number;
  onlyLetters?: boolean;
  onlyDigits?: boolean;
  alphaNumeric?: boolean;
}

// Helper function to create validation rules
const createValidationRules = (validationSchema?: yup.AnyObjectSchema) => {
  if (!validationSchema) return undefined;

  return async (value: any) => {
    try {
      await validationSchema.validate(value);
      return true;
    } catch (error: any) {
      return error.message;
    }
  };
};

// Helper function to process input value
const processInputValue = (
  value: string,
  noCapitalize: boolean,
  onlyLetters?: boolean,
  onlyDigits?: boolean,
  alphaNumeric?: boolean
): string => {
  let processed = value;

  if (!noCapitalize && processed.length === 1) {
    processed = processed.charAt(0).toUpperCase() + processed.slice(1);
  }

  if (onlyLetters) {
    processed = processed.replace(/[^A-Za-z.\s]/g, "");
  }

  if (onlyDigits) {
    processed = processed.replace(/\D+/g, "");
  }
  if (alphaNumeric) {
    processed = processed.replace(/[^A-Za-z0-9.,\-& ]/g, "");
  }

  return processed;
};

// Helper function to calculate padding styles
const getPaddingStyle = (icon?: ReactNode, rightIcon?: ReactElement): CSSProperties => ({
  paddingLeft: icon ? "2.5rem" : undefined,
  paddingRight: rightIcon ? "2.5rem" : undefined,
});

const Input = ({
  name,
  label,
  type = "text",
  validationSchema,
  placeholder,
  onChange,
  value,
  disabled,
  required,
  min,
  max,
  noCapitalize = false,
  icon,
  rightIcon,
  onRightIconClick,
  helperText,
  autoComplete,
  maxLength,
  ...rest
}: InputProps) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();

  const {
    field,
    fieldState: { invalid },
  } = useController({
    name,
    control,
    defaultValue: "",
    rules: {
      validate: createValidationRules(validationSchema),
    },
  });

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const processed = processInputValue(
      e.target.value,
      noCapitalize,
      rest.onlyLetters,
      rest.onlyDigits,
      rest.alphaNumeric
    );

    e.target.value = processed;
    field.onChange(e);
    onChange?.(e);
  };

  const errMessage = errors[name]?.message?.toString();
  const inputClasses = `${COMMON_INPUT_STYLES} ${invalid ? ERROR_STATE : NORMAL_STATE
    } ${rest.className ?? ""}`;
  const paddingStyle = getPaddingStyle(icon, rightIcon);

  const commonProps = {
    placeholder,
    className: inputClasses,
    onChange: handleChange,
    onKeyDown: rest.onKeyDown,
    value: value ?? field.value,
    maxLength,
    disabled,
    style: paddingStyle,
  };

  return (
    <div className="mb-3">
      <label htmlFor={name} className="block mb-1 text-sm font-medium text-gray-800 dark:text-gray-200">
        {label} {required && <span className="text-red-500">*</span>}
      </label>

      <div className="relative">
        {icon && (
          <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
            {icon}
          </div>
        )}

        {rightIcon && (
          <button
            type="button"
            onClick={onRightIconClick}
            aria-label="Toggle visibility"
            className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700"
          >
            {rightIcon}
          </button>
        )}

        {type === "textarea" ? (
          <textarea {...field} {...commonProps} rows={4} />
        ) : (
          <input
            {...field}
            {...commonProps}
            type={type}
            min={min}
            max={max}
            autoComplete={autoComplete}
          />
        )}
      </div>

      {errMessage && (
        <p className="text-red-500 mt-1 text-sm font-medium">
          {errMessage}
        </p>
      )}

      {helperText && !errMessage && (
        <p className="text-gray-500 mt-1 text-xs">{helperText}</p>
      )}
    </div>
  );
};

export default Input;
